// Build-time switch. Leave the image API undeployed (e.g. to avoid hosting the
// ~1.5 GB CLIP model for now) by either unsetting REACT_APP_API_URL or setting
// REACT_APP_DISABLE_GUESS=true. When guessing is off the upload flow skips the
// request entirely and goes straight to manual item selection — no wait.
const API_URL = process.env.REACT_APP_API_URL;
const guessEnabled = !!API_URL && process.env.REACT_APP_DISABLE_GUESS !== "true";

function isGuessEnabled() {
    return guessEnabled;
}

// The vision model only sees a ~512px version of the image (detail: "low"), so
// uploading a full multi-MB phone photo just makes the guess slow. Downscale to
// a small JPEG before sending. This only affects the guess request — the
// full-resolution photo is still what gets stored. Falls back to the original
// if the browser can't decode/resize it (e.g. an exotic format).
const GUESS_MAX_DIM = 768;

async function downscaleForGuess(image, maxDim = GUESS_MAX_DIM) {
    try {
        const bitmap = await createImageBitmap(image);
        const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
        if (scale === 1) {            // already small enough — send as-is
            bitmap.close?.();
            return image;
        }
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
        bitmap.close?.();

        const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.8)
        );
        return blob || image;
    } catch {
        return image;
    }
}

async function classifyImage(image, labels) {
    const small = await downscaleForGuess(image);

    const data = new FormData();

    data.set("image", small, "guess.jpg");
    data.set("labels", JSON.stringify(labels)); // JSON encoded

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
        const response = await fetch(process.env.REACT_APP_API_URL  + "/guess_label", {
            method: "POST",
            body: data,
            signal: controller.signal
        });

        // A 503 (classifier unavailable) is not a guess — treat it as a failure
        // so the caller's catch shows the "unavailable" message.
        if (!response.ok) {
            throw new Error(`guess_label returned ${response.status}`);
        }

        return await response.json();
    } finally {
        clearTimeout(timeout);
    }
}

export { classifyImage, isGuessEnabled, API_URL }